import React, { useEffect, useState } from 'react';
import {
  SupportRequestService, SupportRequestableOrg, apiErrorMessage, formatSupportExpiry,
} from '../services/supportDataService';

interface Props {
  show: boolean;
  onClose: () => void;
  /** Called after a request is sent, with a message to show the support user. */
  onRequested: (message: string) => void;
}

/** Why an organisation can't be asked right now, or null when it can. */
function blockedReason(org: SupportRequestableOrg): string | null {
  if (org.grantState === 'pending') return 'waiting for an admin';
  if (org.grantState === 'active') return `access until ${formatSupportExpiry(org.expiresAt ?? '')}`;
  return null;
}

/**
 * Support side of the request flow (tdp-api docs/support-access-plan.md §5):
 * pick an organisation, say why, choose how long. The reason is shown to the
 * organisation's Admins, who approve or decline inside the app.
 */
const RequestSupportAccessModal: React.FC<Props> = ({ show, onClose, onRequested }) => {
  const [orgs, setOrgs] = useState<SupportRequestableOrg[]>([]);
  const [dayOptions, setDayOptions] = useState<number[]>([1, 3, 7]);
  const [orgId, setOrgId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(3);
  const [ticketRef, setTicketRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setOrgId(''); setReason(''); setTicketRef(''); setError(null);
    setLoading(true);
    SupportRequestService.fetchOrganisations()
      .then(r => {
        setOrgs(r.organisations);
        setDayOptions(r.dayOptions);
        setDays(r.dayOptions.includes(3) ? 3 : r.dayOptions[0]);
      })
      .catch(err => setError(apiErrorMessage(err, 'Could not load organisations.')))
      .finally(() => setLoading(false));
  }, [show]);

  if (!show) return null;

  const reasonLength = reason.trim().length;
  // Said out loud, so a disabled Send never leaves anyone guessing why.
  const missing = orgId === '' ? 'Choose an organisation'
    : reasonLength < 10 ? `Give a reason of at least 10 characters (${reasonLength}/10)`
    : null;
  const canSend = !missing && !sending;

  const send = async () => {
    if (orgId === '') return;
    setSending(true);
    setError(null);
    try {
      const result = await SupportRequestService.requestAccess(orgId, {
        reason: reason.trim(),
        days,
        ticketRef: ticketRef.trim() || undefined,
      });
      const name = orgs.find(o => o.id === orgId)?.name ?? 'the organisation';
      onRequested(result.adminsNotified > 0
        ? `Request sent. ${result.adminsNotified} admin${result.adminsNotified === 1 ? '' : 's'} at ${name} ${result.adminsNotified === 1 ? 'has' : 'have'} been emailed.`
        : `Request sent to ${name}. No admin email could be sent, so let them know to check Organisation settings.`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the request.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="request-support-title" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="request-support-title">Request support access</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={sending} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading ? (
              <p className="text-muted mb-0">Loading organisations…</p>
            ) : (
              <>
                <label className="form-label" htmlFor="rsa-org">Organisation</label>
                <select id="rsa-org" className="form-select mb-3" value={orgId} disabled={sending}
                  onChange={e => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value="">Choose an organisation…</option>
                  {orgs.map(o => {
                    const blocked = blockedReason(o);
                    return (
                      <option key={o.id} value={o.id} disabled={!!blocked}>
                        {o.name}{blocked ? ` (${blocked})` : ''}
                      </option>
                    );
                  })}
                </select>

                <label className="form-label" htmlFor="rsa-reason">Reason</label>
                <textarea id="rsa-reason" className="form-control" rows={3} maxLength={500} value={reason} disabled={sending}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Tuesday schedule not generating, per your email of 12 Oct" />
                <div className="form-text mb-3">
                  The organisation's admins will read this when deciding.
                  {reasonLength < 10 && ` At least 10 characters (${reasonLength}/10).`}
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label" htmlFor="rsa-days">Duration</label>
                    <select id="rsa-days" className="form-select" value={days} disabled={sending} onChange={e => setDays(Number(e.target.value))}>
                      {dayOptions.map(d => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="rsa-ticket">Ticket ref (optional)</label>
                    <input id="rsa-ticket" className="form-control" maxLength={50} value={ticketRef} disabled={sending}
                      onChange={e => setTicketRef(e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            {missing && !loading && <span className="me-auto small text-muted">{missing}</span>}
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={sending}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={send} disabled={!canSend} title={missing ?? undefined}>
              {sending ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestSupportAccessModal;
